import React, { Component } from 'react'
import _ from 'lodash'
import {
  Card,
  FormInput,
  FormInputTextArea,
  Row,
  Col,
  DetailList,
  ButtonContainer,
  Button,
  Alert,
  FormInputSelect,
  ContentSectionBlock
} from 'camino-react'

import { cloudhelpGet, cloudhelpExecute } from 'Utilities/CloudHelp'
import { stripTags } from 'Utilities/StringManipulator'

// --------------------------------------------------------------------------------
// Constants
// --------------------------------------------------------------------------------
const types = {
  client: {
    label: 'Clients',
    noticeableType: 'Client',
    keySelector: 'client'
  },
  ticket: {
    label: 'Tickets',
    noticeableType: 'Ticket',
    keySelector: 'ticket'
  },
  problem: {
    label: 'Problems',
    noticeableType: 'Problem',
    keySelector: 'problem'
  }
}

const AssociatedItemsSelectors = ({
  noticeItems,
  options,
  addNoticeItem,
  type
}) => {
  const twoOrMoreItems = noticeItems.length > 1
  return (
    <div>
      {_.map(noticeItems, noticeItem => (
        <FormInput>
          <div key={noticeItem.id} className="flex flex--centered flex--space-between Select__wrapper-with-padding">
            <div className="flex--grow">
              <FormInputSelect
                value={_.find(options, ({ value: noticeItem.noticeable_id }))}
                options={_.filter(options, ({ disabled }) => (!disabled))}
                isLoading={_.isEmpty(options)}
                onValue={value => noticeItem.update(value)}
                clearable
              />
            </div>
            {twoOrMoreItems && (
              <button
                type="button"
                style={{ border: 'none', background: 'transparent', outline: 'none' }}
                className="lnr lnr-trash2 lnr--hover-blue"
                onClick={() => noticeItem.remove()}
              />
            )}
          </div>
        </FormInput>
      ))}
      <br />
      <Button
        onClick={() => addNoticeItem()}
        size="compact"
        outline
      >
        <span className="lnr lnr-plus lnr-inline" />
        {`Add a ${type}`}
      </Button>
    </div>
  )
}

// --------------------------------------------------------------------------------
// Main Component
// --------------------------------------------------------------------------------
class NoticeForm extends Component {
  constructor (props) {
    super(props)

    const { noticeItems, notice } = this.props
    const { content } = notice

    const initialItems = _.map(types, (type, key) => Object.assign({}, { noticeable_type: type.noticeableType }))

    const noticeItemsWithInitialItems = Object.assign([], [...noticeItems, ...initialItems])

    this.state = {
      fields: {
        noticeItems: noticeItemsWithInitialItems,
        content: content || ''
      },
      clients: [],
      submitButton: {
        text: 'Save Notice',
        disabled: false
      },
      errors: []
    }
    this.loadClients = this.loadClients.bind(this)
    this.attachHandlers = this.attachHandlers.bind(this)
    this.addNoticeItem = this.addNoticeItem.bind(this)
    this.updateNoticeItem = this.updateNoticeItem.bind(this)
    this.removeNoticeItem = this.removeNoticeItem.bind(this)
    this.submitForm = this.submitForm.bind(this)
  }

  componentWillMount () {
    this.loadClients()
  }

  handleContentChange = (value) => {
    this.setState((prevState) => {
      const { fields } = prevState

      const newFields = Object.assign({}, fields, { content: value })

      return {
        fields: newFields,
        errors: []
      }
    })
  }

  loadClients () {
    const { paths } = this.props
    const { apiClientsPath } = paths
    cloudhelpGet(apiClientsPath)
      .then(({ data }) => {
        this.setState({ clients: data })
      })
  }

  attachHandlers (noticeItems) {
    return (
      _.map(noticeItems, (noticeItem, index) => Object.assign({}, noticeItem, {
        remove: () => this.removeNoticeItem(index),
        update: value => this.updateNoticeItem(index, value)
      }))
    )
  }

  addNoticeItem (type) {
    this.setState((prevState) => {
      const { fields } = prevState
      const { noticeItems } = fields
      const newNoticeItems = [...noticeItems, { noticeable_type: type }]
      const newFields = Object.assign({}, fields, { noticeItems: newNoticeItems })

      return { fields: newFields }
    })
  }

  updateNoticeItem (indexToUpdate, input) {
    this.setState((prevState) => {
      const { fields } = prevState
      const { noticeItems } = fields

      const noticeItem = noticeItems[indexToUpdate]
      const newNoticeItem = Object.assign({}, noticeItem, { noticeable_id: input })
      const newNoticeItems = Object.assign([...noticeItems], { [indexToUpdate]: newNoticeItem })
      const newFields = Object.assign({}, fields, { noticeItems: newNoticeItems })

      return { fields: newFields }
    })
  }

  removeNoticeItem (indexToRemove) {
    this.setState((prevState) => {
      const { fields } = prevState
      const { noticeItems } = fields

      const newNoticeItems = _.filter(noticeItems, (noticeItem, index) => indexToRemove !== index)
      const newFields = Object.assign({}, fields, { noticeItems: newNoticeItems })

      return { fields: newFields }
    })
  }

  submitForm () {
    const { fields } = this.state
    const { method, paths } = this.props
    const { noticeRequestPath } = paths

    const noticeItems = _.map(fields.noticeItems, (
      { id, noticeable_type, noticeable_id }
    ) => (
      noticeable_id && { id, noticeable_type, noticeable_id }
    ))

    const dataForSubmit = {
      notice: {
        content: stripTags(fields.content) ? fields.content : '',
        notices_items: noticeItems
      }
    }
    this.setState({
      submitButton: {
        text: 'Saving Notice ...',
        disabled: true
      },
      alert: {},
      errors: []
    })

    cloudhelpExecute(method, noticeRequestPath, dataForSubmit)
      .then(({ data }) => {
        this.setState({
          submitButton: {
            text: 'Notice Saved',
            disabled: true
          }
        })
        window.location.href = data.redirect_url
      })
      .catch(({ response }) => {
        this.setState({
          submitButton: {
            text: 'Save Notice',
            disabled: false
          },
          alert: {
            text: response.data.message || 'There has been an error',
            type: 'error'
          },
          errors: response.data.errors || []
        })
      })
  }

  render () {
    const {
      attachHandlers,
      addNoticeItem,
      submitForm
    } = this

    const {
      fields,
      clients,
      submitButton: {
        disabled: submitDisabled,
        text: submitText
      },
      alert,
      errors
    } = this.state

    const { noticeItems } = fields

    const {
      currentTickets,
      openProblems,
      paths
    } = this.props

    const { noticesPath } = paths

    const options = {
      client: clients,
      problem: openProblems,
      ticket: currentTickets
    }

    const noticeItemsWithHandlers = attachHandlers(noticeItems)

    return (
      <Row>
        <Col>
          { alert && alert.text && (
            <Alert type={alert.type}>
              {alert.text}
            </Alert>
          ) }
          <Row>
            <Col>
              <DetailList
                list={[
                  {
                    title: 'Clients, Tickets and Problems',
                    content: 'Assign this notice to one or multiple clients, tickets or problems'
                  }
                ]}
              />
            </Col>
            <Col>
              <Card padding={false} allowOverflow>
                {_.map(types, (type) => {
                  const typeNoticeItems = _.filter(
                    noticeItemsWithHandlers, { noticeable_type: type.noticeableType }
                  )

                  const optionIds = _.map(typeNoticeItems, noticeItem => noticeItem.noticeable_id)

                  const typeOptions = _.map(options[type.keySelector], ({
                    id, short_name: shortName, title, issue
                  }) => ({
                    label: `#${id} - ${shortName || title || issue}`,
                    value: id,
                    disabled: _.includes(optionIds, id)
                  }))

                  return (
                    <ContentSectionBlock>
                      <FormInput label={type.label}>
                        <AssociatedItemsSelectors
                          noticeItems={typeNoticeItems}
                          options={typeOptions}
                          addNoticeItem={() => { addNoticeItem(type.noticeableType) }}
                          type={type.noticeableType}
                        />
                      </FormInput>
                    </ContentSectionBlock>
                  )
                })}
              </Card>
            </Col>
          </Row>
          <hr />
          <Row>
            <Col>
              <DetailList
                list={[
                  {
                    title: 'Notice',
                    content: 'Describe the comment which will be assigned to each client, ticket or problem'
                  }
                ]}
              />
            </Col>
            <Col>
              <Card>
                <FormInput label="Content">
                  <FormInputTextArea
                    value={fields.content}
                    rows={5}
                    onValue={this.handleContentChange}
                    errors={errors.content}
                  />
                </FormInput>
              </Card>
            </Col>
          </Row>
          <Row>
            <Col>
              <ButtonContainer center spaced>
                <a href={noticesPath} className="button button--outline">
                  Cancel
                </a>
                <Button onClick={() => submitForm()} disabled={submitDisabled}>
                  {submitText}
                </Button>
              </ButtonContainer>
            </Col>
          </Row>
        </Col>
      </Row>
    )
  }
}

export default NoticeForm
